import React, { useEffect, useState } from 'react';
import { InjectedRouter, RouterState, PlainRoute } from 'react-router';
import Cookies from 'lib/Cookies';
import { withRouter } from 'app/context/Router';
import Container from 'components/Container';
import { TutoringStateMachine } from 'lib/TutoringStateMachine/StateMachine';
import CoachingIsolatedViewHeader from 'coaching/components/CoachingIsolatedViewHeader';
import TutoringSelfServeViewBreadcrumbs, {
  BreadcrumbSteps,
  getBreadcrumbConfig,
  stepsToRoutes,
} from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewBreadcrumbs';
import {
  getPurchaseCompleted,
  getStudentDetails,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';
import { clearSignupStorage } from 'lib/TutoringStateMachine/SignUpStorage';
import CoachingConstants from 'coaching/CoachingConstants';

export type Props = {
  router: InjectedRouter & RouterState;
  route: PlainRoute;
};

type TutoringSelfServeViewState = {
  currentStep: BreadcrumbSteps;
  stateMachine?: TutoringStateMachine;
};

function TutoringSelfServeView(props: Props) {
  const { router, route } = props;
  const locationPath: string | undefined = router.location?.pathname;
  const [state, setState] = useState<TutoringSelfServeViewState>({ currentStep: 'Start' });
  const allowedLocationsAfterCompletion: string[] = [
    CoachingConstants.TUTORING_SUMMARY,
    CoachingConstants.TUTORING_LINK_STUDENT,
  ];

  // Remove old flow cookie on mount. This prevents the possibility of redirecting
  // to /coaching/review after visiting the new flow. This can be removed once
  // the cookie no longer does anything i.e. once the old flow is removed
  useEffect(() => {
    Cookies.expire(CoachingConstants.IS_INTENDED_COACHING_USER_COOKIE);
  }, []);

  useEffect(() => {
    if (getPurchaseCompleted() && !allowedLocationsAfterCompletion.includes(locationPath)) {
      clearSignupStorage();
    }
    const stateMachine = new TutoringStateMachine(props.router);
    setState({ ...state, stateMachine });
  }, [locationPath]); // If the location changes (back button), create a new state machine to render the correct component

  const currentState = state.stateMachine?.currentState;

  const goToNextState = state.stateMachine?.goToNextState.bind(state.stateMachine) ?? (() => null);
  const CurrentStateComponent = currentState?.component;
  const componentProps = { goToNextState, router, route };
  const enabledStates = state.stateMachine?.enabledStates()?.map(s => s.name) ?? [];
  return (
    <div className="CoachingIsolatedView">
      <Container size="medium">
        <CoachingIsolatedViewHeader />
        <TutoringSelfServeViewBreadcrumbs
          stepConfig={getBreadcrumbConfig(state.stateMachine?.currentState!.name, enabledStates)}
          stepSelected={step => {
            const stepRoute = stepsToRoutes[step];
            stepRoute && props.router.push(stepRoute);
          }}
        />
        {CurrentStateComponent && <CurrentStateComponent {...componentProps} />}
      </Container>
    </div>
  );
}

export { TutoringSelfServeView };

export default withRouter(TutoringSelfServeView);
